import { useState, useEffect } from 'react';
import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _uniq from 'lodash/uniq';
import _difference from 'lodash/difference';
import _keyBy from 'lodash/keyBy';
import { useConfigService, useStore } from '@thd-nucleus/experience-context';
import { fulfillmentApi, productsApi } from '../api/graphQL';
import { useDesignStore } from '../store/design/context';
import { addPricingAndFulfillment } from './utils';
import { tryGetDesign } from '../api/design';
import { tryGetQueryParam } from '../utils/queryParams';
import { getLocalizerZipCode } from '../utils/fulfillment-utils';
import logNewRelicCustom from '../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../constants';

export const useAsyncServiceGCC = (asyncFn, ...args) => {
  const [data, setData] = useState({ data: null, ready: false });
  const host = useConfigService('global-custom-commerce:host');
  useEffect(() => {
    const fetchData = async () => {
      const response = await asyncFn(host, ...args);
      const isReady = !_isNil(response);
      setData({
        data: response,
        ready: isReady
      });
    };
    if (!_isEmpty(...args)) {
      fetchData();
    }
  }, [...args]);

  return [data];
};

export const useAsyncCollectionServiceGCC = (asyncFn, ...args) => {
  const [data, setData] = useState({ data: null, ready: false });
  const host = useConfigService('global-custom-commerce:host');

  useEffect(() => {
    const fetchData = async () => {
      const response = await asyncFn(host, ...args);
      const isReady = !_isNil(response);
      if (_isNil(response)) {
        logNewRelicCustom(NEW_RELIC_EVENTS.ASYNC_COLLECTION_SERVICE_GCC, {
          asyncCollectionServiceGCC: {
            api: `${host}/${asyncFn.name}`,
            errorMessage: 'Not found',
            omsId: args
          }
        });
        window.location.replace('https://www.homedepot.com/c/kitchen_cabinet_collection_by_colors');
      }
      setData({
        data: response,
        ready: isReady
      });
    };
    if (!_isEmpty(...args)) {
      fetchData();
    }
  }, [...args]);

  return [data];
};

export const useAsyncServiceTHD = (itemIds) => {
  const [data, setData] = useState({ data: null, ready: false });
  const fulfillmentBaseUrl = useConfigService('graphQL:fulfillmentBaseUrl');
  const graphBaseUrl = useConfigService('graphQL:baseUrl');
  const productsPerBatch = useConfigService('graphQL:productsPerBatch');
  const fulfillmentPerBatch = useConfigService('graphQL:fulfillmentPerBatch');
  const store = useStore();
  const { storeId = '8119' } = store;
  const zipCode = getLocalizerZipCode();
  const visualizedDesignSkus = useDesignStore((state) => _map(state.design.products, (item) => item?.Sku));
  const nonVisualizedDesignSkus = useDesignStore((state) => _map(state.design.cart, (item) => item?.Sku));
  const designId = tryGetQueryParam('designId');
  const host = useConfigService('global-custom-commerce:host');

  useEffect(() => {

    const getProducts = async (skus) => {
      if (!_isEmpty(skus)) {
        const response = await productsApi(graphBaseUrl, productsPerBatch, skus, zipCode);
        return response;
      }
      return [];
    };

    const getFulfillment = async (skus) => {
      if (!_isEmpty(skus)) {
        const response = await fulfillmentApi(fulfillmentBaseUrl, fulfillmentPerBatch, skus, storeId, zipCode);
        return response;
      }
      return [];
    };

    const resolver = async (skus) => {

      const productData = await getProducts(skus);
      const pricingAndFulfillmentData = await getFulfillment(skus);

      let finalVisualizedSkus = visualizedDesignSkus;
      let finalNonVisualizedSkus = nonVisualizedDesignSkus;

      // This is to handle the case when design is not available or not hydrated (aka cloned design)
      if (_isEmpty(visualizedDesignSkus) && _isEmpty(nonVisualizedDesignSkus)) {
        const design = await tryGetDesign(host, designId);
        finalVisualizedSkus = _map(design?.products, (item) => String(item?.sku));
        finalNonVisualizedSkus = _map(design?.cart, (item) => String(item?.sku));
      }

      const designSkus = _uniq([
        ...finalVisualizedSkus,
        ...finalNonVisualizedSkus
      ]);

      const promoSkus = _difference(designSkus, skus);

      if (!_isEmpty(promoSkus)) {
        const promoProductData = await getProducts(promoSkus) || [];
        const promoFulfillmentData = await getFulfillment(promoSkus) || [];

        const productDataAndPromoDataWithFulfillment = addPricingAndFulfillment(
          [...productData, ...promoProductData],
          _keyBy([...pricingAndFulfillmentData, ...promoFulfillmentData], 'itemId')
        );

        return setData({
          data: _keyBy(productDataAndPromoDataWithFulfillment, 'itemId'),
          ready: true
        });
      }

      const productDataWithPricingAndFulfillment = addPricingAndFulfillment(
        productData, _keyBy(pricingAndFulfillmentData, 'itemId')
      );

      return setData({
        data: _keyBy(productDataWithPricingAndFulfillment, 'itemId'),
        ready: true
      });

    };

    if (_isEmpty(itemIds)) {
      return;
    }

    const uniqProductsAndCart = _uniq([
      ...itemIds,
      ...visualizedDesignSkus,
      ...nonVisualizedDesignSkus
    ]);

    resolver(uniqProductsAndCart);

  }, [itemIds]);

  return [data];
};

export { default as useProjectLoader } from './useProjectLoader';
export { default as useCollectionMatching } from './useCollectionMatching';
